/**
 * Global search contract — v1 is employee search only.
 *
 * Deliberately NOT `paginatedSchema()` the way employee.contract.ts's `list` is (see that file's
 * header, convention 3): this is a command-palette-style "jump to" endpoint, not a directory
 * browse. It returns a small, capped set of hits for the current query rather than a page a caller
 * would ever paginate through. If a second entity type (departments, docs, leave requests) joins
 * global search later, the response should grow additional optional arrays alongside `employees`,
 * not a cursor.
 *
 * `SearchHitDto` mirrors the relevant slice of `employeeSchema` in employee.contract.ts — same
 * field names/types where they overlap (`photoUrl` nullable, `designation` nullable) — so a client
 * doesn't need a second mental model for "an employee" depending on which endpoint returned it.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const searchHitSchema = z.object({
  employmentId: uuidSchema,
  personId: uuidSchema,
  displayName: z.string(),
  designation: z.string().nullable(),
  photoUrl: z.string().url().nullable(),
});
export type SearchHitDto = z.infer<typeof searchHitSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const searchQuerySchema = z.object({
  /** A 1-character query is too broad to be useful and too expensive to run on every keystroke. */
  q: z.string().trim().min(2).max(200),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const searchContract = c.router(
  {
    search: {
      method: 'GET',
      path: '/',
      query: searchQuerySchema,
      responses: {
        200: z.object({ employees: z.array(searchHitSchema) }),
        401: errorBodySchema,
      },
      summary:
        'Global search across the caller’s visible scope — v1: employees only, capped, unpaginated',
      metadata: routeMeta('employee:read'),
    },
  },
  { pathPrefix: '/search' },
);
